import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { AED, fmtDateTime, fmtShort, downloadReceipt, cancelPenalty, settleBalance } from "@/lib/api";
import { txnStatusLabel, fmtBal } from "@/lib/statusLabels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import SlotPicker from "@/components/SlotPicker";
import ConcernModal from "@/components/ConcernModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Package, ShoppingBag, CheckCircle2, AlertTriangle, Clock, Hourglass, Circle, Download } from "lucide-react";
import { DubaiAreaSelect } from "@/components/DubaiAreaSelect";

const STATUS_COLORS = {
  ESCROW_HELD: "bg-blue-100 text-blue-700", COMPLETED: "bg-emerald-100 text-emerald-700",
  DISPUTED: "bg-red-100 text-red-700", REFUNDED: "bg-slate-100 text-slate-700",
};
const STEPS = [
  ["payment_confirmed", "Payment confirmed"], ["collection_scheduled", "Collection scheduled"],
  ["item_collected", "Item collected"], ["out_for_delivery", "Out for delivery"],
  ["delivered", "Delivered"], ["completed", "Completed"],
];
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const fmtTS = fmtDateTime;
const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);
const Thumb = ({ s }) => {
  const src = imgSrc(s);
  return src
    ? <img src={src} alt="" className="h-14 w-14 rounded-xl object-cover" />
    : <div className="h-14 w-14 rounded-xl bg-slate-100" aria-hidden="true" />;
};

function useCountdown(expiresAt) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setSecs(Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return secs;
}

const noPayout = (t) => ["REFUNDED", "CANCELLED"].includes(t.status) || ["due", "succeeded", "failed"].includes(t.refund_status);
const isFree = (t) => t.listing_type === "free";
const concernUpheld = (t) => t.refund_no_return || (t.timeline || []).some((x) => x.step === "concern_upheld_return" || x.step === "concern_upheld_refund");
const displayStatus = (t) => {
  if (t.status === "DISPUTED") {
    const done = Object.fromEntries((t.timeline || []).map((x) => [x.step, x.at]));
    if (t.refund_no_return || done.concern_upheld_refund) return "Refund approved";
    if (done.concern_upheld_return || t.return_status) return "Return in progress";
    return "Disputed";
  }
  return txnStatusLabel(t.status);
};
const returnActive = (t) => t.status !== "CANCELLED" && ["return_requested", "return_scheduled", "return_failed", "return_collected", "return_completed"].includes(t.return_status);
const DEDUCTION = 98;
const deductionWarning = (t) => {
  const paid = Math.round((t.total || 0) * 100) / 100;
  if (paid >= DEDUCTION) return `A second failed delivery cancels the order and AED ${DEDUCTION} is deducted from your refund to cover the delivery and return.`;
  return `A second failed delivery cancels the order. AED ${DEDUCTION} covers the delivery and return — you've paid AED ${paid}, so you'd owe AED ${Math.round((DEDUCTION - paid) * 100) / 100}.`;
};
const EVENT_LABELS = {
  delivery_failed: "Delivery attempt failed", delivery_failed_courier: "Delivery attempt failed", delivery_failed_final: "Delivery attempt failed",
  courier_reschedule_collection: "Collection rescheduled — courier issue", courier_reschedule_delivery: "Delivery rescheduled — courier issue",
};
const eventRows = (t) => (t.timeline || []).filter((x) => EVENT_LABELS[x.step]).map((x) => ({ label: EVENT_LABELS[x.step], at: x.at, event: true }));
const byTime = (rows) => [...rows].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
// Every occurrence of a step is kept, so a rescheduled step keeps its original time too.
const stepRows = (t, steps) => {
  const labels = Object.fromEntries(steps); const seen = {};
  return (t.timeline || []).filter((x) => labels[x.step]).map((x) => {
    const again = seen[x.step]; seen[x.step] = true;
    return { key: x.step, label: again ? `${labels[x.step]} — new time` : labels[x.step], at: x.at };
  });
};

function Rows({ rows, label }) {
  return (
    <ol className="mt-3 space-y-1.5 rounded-xl bg-slate-50 p-3" data-testid="order-timeline">
      {label && <li className="text-xs font-semibold text-slate-700">{label}</li>}
      {rows.map((r, i) => (
        <li key={i} className="flex items-center gap-2 text-sm" data-testid={r.event ? "timeline-event" : "timeline-step"}>
          {r.at ? (r.event ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />) : <Circle className="h-4 w-4 text-slate-300" />}
          <span className={r.at ? "font-medium text-slate-800" : "text-slate-400"}>{r.label}</span>{" "}
          {r.at && <span className="ml-auto shrink-0 pl-3 text-xs text-slate-400">{fmtTS(r.at)}</span>}
        </li>
      ))}
    </ol>
  );
}

function TimelineList({ steps, done, label, tone = "amber" }) {
  const tones = { amber: "bg-amber-50 text-amber-800", red: "bg-red-50 text-red-800", slate: "bg-slate-50 text-slate-700" };
  return (
    <ol className={`mt-3 space-y-1.5 rounded-xl p-3 ${tones[tone].split(" ")[0]}`} data-testid="order-timeline">
      {label && <li className={`text-xs font-semibold ${tones[tone].split(" ")[1]}`}>{label}</li>}
      {steps.map(([key, text]) => {
        const at = done[key];
        return (
          <li key={key} className="flex items-center gap-2 text-sm">
            {at ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
            <span className={at ? "font-medium text-slate-800" : "text-slate-400"}>{text}</span>{" "}
            {at && <span className="ml-auto shrink-0 pl-3 text-xs text-slate-400">{fmtTS(at)}</span>}
          </li>
        );
      })}
    </ol>
  );
}

function OrderTimeline({ t, seller }) {
  const done = Object.fromEntries((t.timeline || []).map((x) => [x.step, x.at]));
  let concern = null;
  if (done.disputed) {
    const steps = [["disputed", "Concern raised"]];
    if (done.concern_upheld_return) steps.push(["concern_upheld_return", "Concern upheld — return requested"]);
    else if (done.concern_upheld_refund) steps.push(["concern_upheld_refund", "Concern upheld — refund approved"]);
    else if (done.concern_reviewed_stands) steps.push(["concern_reviewed_stands", "Concern reviewed — sale stands"]);
    concern = <TimelineList steps={steps} done={done} label="Dispute history" tone="red" />;
  }
  if (t.refund_no_return) {
    const header = t.status === "REFUNDED"
      ? (seller ? "Refund sent — the buyer keeps the item" : "Refund sent — you keep the item")
      : (seller ? "Refund approved — the buyer keeps the item" : "Refund approved — you keep the item");
    return <>{concern}<TimelineList steps={[["concern_upheld_refund", "Refund approved"], ["refund_issued", "Refund issued"]]} done={done} label={header} /></>;
  }
  if (t.status === "CANCELLED") {
    const rows = stepRows(t, [["payment_confirmed", "Payment confirmed"], ["collection_scheduled", "Collection scheduled"], ["item_collected", "Item collected"]]);
    rows.push(...eventRows(t), { label: "Cancelled", at: done.cancelled || t.cancelled_at });
    if (t.refund_status === "succeeded") rows.push({ label: "Refund sent", at: done.refund_issued || t.refunded_at });
    else if (t.refund_outcome && !seller) rows.push({ label: t.refund_outcome, at: done.delivery_failed_final || done.cancelled || t.cancelled_at });
    else if (t.refund_status) rows.push({ label: "Refund due", at: done.refund_due || done.cancelled || t.cancelled_at });
    if (done.returned_to_seller || t.returned_to_seller_at) rows.push({ label: seller ? "Returned to you" : "Returned to seller", at: done.returned_to_seller || t.returned_to_seller_at });
    return <>{concern}<Rows rows={byTime(rows)} label="Order cancelled" /></>;
  }
  if (t.status === "REFUNDED" || t.status === "DISPUTED") {
    const returnDecided = t.status === "REFUNDED" || done.concern_upheld_return;
    if (!returnDecided) {
      return <>{concern}<div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm font-medium text-amber-800" data-testid="concern-review-note">Concern under review — payment on hold</div></>;
    }
    const RSTEPS = [["return_requested", "Return requested"], ["return_collected", "Return collected from buyer"], ["refund_issued", "Refund issued"], ["returned_to_seller", "Returned to seller"], ["transaction_closed", "Transaction closed"]];
    const header = t.status === "REFUNDED" ? "Return in progress — refund sent" : "Return requested — payment on hold";
    return <>{concern}<TimelineList steps={RSTEPS} done={done} label={header} /></>;
  }
  const doneRows = stepRows(t, STEPS);
  const pendingRows = STEPS.filter(([k]) => !done[k]).map(([, l]) => ({ label: l }));
  return <>{concern}<Rows rows={[...byTime([...doneRows, ...eventRows(t)]), ...pendingRows]} /></>;
}

export default function Orders() {
  const { user, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = (tabParam === "sales" || tabParam === "purchases" || tabParam === "pending-sales" || tabParam === "pending-purchases") ? tabParam : "purchases";
  const [txns, setTxns] = useState([]);
  const [pending, setPending] = useState({ purchases: [], sales: [] });
  const [slot, setSlot] = useState(null); // {kind, tid}
  const [areaFor, setAreaFor] = useState(null);
  const [areaVal, setAreaVal] = useState("");
  const openSlot = (s) => { if (user.area || user.address?.area) setSlot(s); else { setAreaVal(""); setAreaFor(s); } };
  const saveArea = async () => {
    try {
      const { data } = await api.put("/auth/profile", { area: areaVal });
      setUser(data); const s = areaFor; setAreaFor(null); setSlot(s);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your area"); }
  };
  const [collectionConfirm, setCollectionConfirm] = useState(null); // { tid, val }
  const [concern, setConcern] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null); // buyer cancel-after-failed txn
  const [sellerCancel, setSellerCancel] = useState(null); // seller cancel sale (before slot) txn
  const [cancelReason, setCancelReason] = useState("");

  const load = async () => {
    const [t, p] = await Promise.all([api.get("/transactions"), api.get("/pending")]);
    setTxns(t.data); setPending(p.data);
  };
  useEffect(() => { load(); }, []);

  const [happy, setHappy] = useState(null);
  const approve = async (id) => {
    try { await api.post(`/transactions/${id}/approve`); toast.success("Purchase complete — payment released to the seller"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not complete purchase"); }
    setHappy(null); load();
  };
  const buyerCancel = async (id) => {
    try { await api.post(`/transactions/${id}/buyer-cancel`); toast.success("Order cancelled — your refund is being processed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel this order"); }
    setCancelConfirm(null); load();
  };
  const sellerCancelSale = async () => {
    if (!sellerCancel || !cancelReason) return;
    try {
      const { data } = await api.post(`/transactions/${sellerCancel.id}/seller-cancel`, { reason: cancelReason });
      toast.success(`Sale cancelled — AED ${data.penalty} cancellation fee applied`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel this sale"); }
    setSellerCancel(null); setCancelReason(""); load();
  };
  const submitSlot = async (val) => {
    const { kind, tid } = slot;
    if (kind === "collection_slot") { setCollectionConfirm({ tid, val, courierString: slot.courierString }); setSlot(null); return; }    await postSlot(kind, tid, val);
    setSlot(null);
  };
  const postSlot = async (kind, tid, val) => {
    const map = { collection_slot: "collection-slot", delivery_slot: "delivery-slot", return_slot_buyer: "return-slot-buyer", return_slot_seller: "return-slot-seller", delivery_rebook: "delivery-rebook-slot", dr_receipt: "delivery-return-slot" };
    try { await api.post(`/transactions/${tid}/${map[kind]}`, val); toast.success("Slot confirmed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    load();
  };

  const purchases = txns.filter((t) => t.buyer_id === user.id);
  const sales = txns.filter((t) => t.seller_id === user.id);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="mb-6 font-heading text-2xl font-bold text-slate-900">My Orders</h1>
      <Tabs defaultValue={initialTab} key={initialTab}>
        <TabsList className="flex max-w-full justify-start overflow-x-auto rounded-full">
          <TabsTrigger value="purchases" data-testid="tab-purchases" className="shrink-0 whitespace-nowrap rounded-full"><ShoppingBag className="mr-1 h-4 w-4" /> Purchases ({purchases.length})</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales" className="shrink-0 whitespace-nowrap rounded-full"><Package className="mr-1 h-4 w-4" /> Sales ({sales.length})</TabsTrigger>
          <TabsTrigger value="pending-purchases" data-testid="tab-pending-purchases" className="shrink-0 whitespace-nowrap rounded-full"><Hourglass className="mr-1 h-4 w-4" /> Pending Purchases ({pending.purchases.length})</TabsTrigger>
          <TabsTrigger value="pending-sales" data-testid="tab-pending-sales" className="shrink-0 whitespace-nowrap rounded-full"><Clock className="mr-1 h-4 w-4" /> Pending Sales ({pending.sales.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending-purchases" className="mt-4 space-y-3">
          {pending.purchases.length === 0 && <Empty text="No pending purchases." />}
          {pending.purchases.map((p) => <PendingPurchaseRow key={p.listing_id} p={p} />)}
        </TabsContent>
        <TabsContent value="pending-sales" className="mt-4 space-y-3">
          {pending.sales.length === 0 && <Empty text="No pending sales." />}
          {pending.sales.map((p) => <PendingSaleRow key={p.listing_id} p={p} />)}
        </TabsContent>

        <TabsContent value="purchases" className="mt-4 space-y-3">
          {purchases.length === 0 && <Empty text="No purchases yet." />}
          {purchases.map((t) => (
            <div key={t.id} className="rounded-2xl border border-slate-200 p-4" data-testid="order-row">
              <OrderHead t={t} />
              <OrderTimeline t={t} />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid="download-receipt-btn" className="rounded-full"
                  onClick={async () => {
                    try { await downloadReceipt(t.id, `CR-${t.id.slice(0, 8).toUpperCase()}`); }
                    catch (e) { toast.error(e.response?.status === 403 ? "You can only download your own receipts" : "Could not download receipt"); }
                  }}>
                  <Download className="mr-1 h-4 w-4" /> Download receipt
                </Button>
                {(() => {
                  const k = buyerStatusKey(t);
                  const dd = t.delivery_date || t.delivery_slot?.date;
                  const dw = t.delivery_window || t.delivery_slot?.label;
                  if (k === "awaiting_delivery_slot") return (
                    <div className="w-full" data-testid="buyer-status-awaiting-delivery">
                      <p className="mb-2 text-xs font-medium text-slate-700">The seller has confirmed their collection slot. Please select your delivery time slot.</p>
                      <Button size="sm" data-testid="buyer-select-delivery" className="rounded-full bg-primary" onClick={() => openSlot({ kind: "delivery_slot", tid: t.id, collectionSlot: t.collection_slot, freeItem: t.no_return, courierString: t.delivery_courier })}>Select Delivery Slot</Button>
                    </div>
                  );
                  if (k === "delivered_inspect") return (
                    <div className="w-full space-y-2" data-testid="buyer-status-delivered">
                      <p className="text-xs font-medium text-slate-700">{t.leave_at_door
                        ? "Your item has been left at your door. If there's a problem with the item itself, you can raise a concern within 24 hours. After that, the sale is final."
                        : "Your item has been delivered. You have 24 hours to inspect it and raise any concerns. After that the sale is final."}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" data-testid="order-approve" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setHappy(t)}><CheckCircle2 className="mr-1 h-4 w-4" /> I'm happy with my purchase</Button>
                        <Button size="sm" variant="outline" data-testid="order-concern" className="rounded-full text-red-600" onClick={() => setConcern({ tid: t.id, lad: !!t.leave_at_door })}><AlertTriangle className="mr-1 h-4 w-4" /> Raise a concern</Button>
                      </div>
                    </div>
                  );
                  if (k === "cancelled" && (t.balance_owed_created || 0) > 0) return <BalanceOwedNote t={t} owing={(user.balance_owed || 0) > 0} />;
                  const msg = buyerMessage(t, dd, dw);
                  if (!msg) return null;
                  if (t.refund_status && (k === "cancelled" || k === "refunded")) return null;
                  const cls = (k === "cancelled" || k === "refunded") ? "bg-red-50 text-red-700"
                    : (k === "completed" || k === "delivered_door" || k === "delivery_slot_confirmed") ? "bg-emerald-50 text-emerald-700"
                    : (k === "awaiting_collection_slot") ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-700";
                  return <div className={`w-full rounded-xl px-3 py-2 text-xs font-medium ${cls}`} data-testid={`buyer-status-${k}`}>{msg}</div>;
                })()}
                {t.refund_status === "due" && (
                  <div className="w-full rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="buyer-refund-processing">
                    Your refund of {AED(t.refund_due_amount ?? t.refund_amount)} is being processed. You'll receive an email once it's been sent.
                  </div>
                )}
                {t.refund_status === "succeeded" && t.stripe_refund_id && (
                  <div className="w-full rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700" data-testid="buyer-refund-sent">
                    Your refund of {AED(t.refund_stripe_amount ?? t.refund_amount)} has been sent. It will appear in your account within 5 to 10 business days.
                  </div>
                )}
                {t.status === "ESCROW_HELD" && (t.collection_failed_attempts || 0) >= 1 && t.collection_status !== "collected" && !["out_for_delivery", "delivered", "completed"].includes(t.logistics_status) && (
                  <button type="button" data-testid="buyer-cancel-link" onClick={() => setCancelConfirm(t)} className="w-full text-left text-xs font-medium text-red-600 underline hover:text-red-700">
                    Cancel this order and get a refund
                  </button>
                )}
                {t.delivery_rebook_required && (
                  <div className="w-full space-y-2" data-testid="buyer-delivery-rebook">
                    <p className="text-xs font-medium text-slate-700" data-testid="buyer-delivery-rebook-text">{t.delivery_failed_fault === "courier"
                      ? "Your delivery needs a new time because of an issue on our side. There's no charge. Please choose a new delivery time."
                      : `We couldn't deliver your item. Please choose a new delivery time within 72 hours. ${deductionWarning(t)}`}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" data-testid="buyer-delivery-rebook-btn" className="rounded-full bg-primary" onClick={() => openSlot({ kind: "delivery_rebook", tid: t.id, freeItem: t.no_return, alreadyLad: !!t.leave_at_door })}>Choose new delivery time</Button>
                    </div>
                  </div>
                )}
                {returnActive(t) && !t.refund_no_return && (t.return_collection_failed_attempts || 0) > 0 && t.return_status !== "return_collected" && (
                  <div className="w-full rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="buyer-return-attempts">
                    Failed return collections: {t.return_collection_failed_attempts}.
                    {(t.return_collection_deduction || 0) > 0 && <> AED {Math.round(t.return_collection_deduction)} will be deducted from your refund.</>}
                    {t.return_rebook_deadline && <> Please book a new collection by {fmtShort(t.return_rebook_deadline)}.</>}
                  </div>
                )}
                {returnActive(t) && !t.refund_no_return && !t.return_buyer_slot && (
                  <Button size="sm" data-testid="buyer-return-slot" className="rounded-full bg-primary" onClick={() => openSlot({ kind: "return_slot_buyer", tid: t.id })}>Select Return Slot</Button>
                )}
                {returnActive(t) && !t.refund_no_return && t.return_buyer_slot && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600" data-testid="buyer-return-set">Return handover: {fmtShort(t.return_buyer_slot.date)} · {t.return_buyer_slot.label}</span>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="sales" className="mt-4 space-y-3">
          {sales.length === 0 && <Empty text="No sales yet." />}
          {sales.map((t) => (
            <div key={t.id} className="rounded-2xl border border-slate-200 p-4" data-testid="sale-row">
              <OrderHead t={t} seller />
              <OrderTimeline t={t} seller />
              {t.payout_blocker && (
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-amber-50 p-3" data-testid="seller-payout-prompt">
                  <p className="text-sm font-medium text-amber-800">Your {AED(t.payout_amount)} is ready — add your details to get paid</p>
                  <Button size="sm" className="rounded-full bg-primary" data-testid="seller-payout-add-details" onClick={() => window.location.assign("/payments")}>Add details</Button>
                </div>
              )}
              {concernUpheld(t) ? (
                <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs font-medium text-slate-600" data-testid="seller-no-payout-upheld">No payout — concern upheld</div>
              ) : noPayout(t) ? (
                <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs font-medium text-slate-600" data-testid="seller-no-payout">
                  {t.status === "CANCELLED" ? "No payout — this order was cancelled" : "No payout — the buyer was refunded"}
                </div>
              ) : t.status === "DISPUTED" ? (
                <div className="mt-2 rounded-xl bg-amber-50 p-3 text-xs font-medium text-amber-800" data-testid="seller-payout-hold">Payout on hold — concern under review</div>
              ) : isFree(t) ? (
                <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs font-medium text-slate-600" data-testid="seller-free-no-payout">Free item — no payout</div>
              ) : !t.seller_paid && t.seller_deposit_shortfall > 0 ? (
                <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600" data-testid="payout-breakdown-topup">
                  Sale {AED(t.agreed_price)} − delivery {AED(t.delivery_fee ?? 49)} − SellStuff fees {AED((t.platform_commission || 0) + (t.processing_fee || 0))} − deposit top-up {AED(Math.min(t.seller_deposit_shortfall, t.seller_payout || 0))} = <span className="font-semibold text-slate-800" data-testid="payout-to-bank">{AED((t.seller_payout || 0) - Math.min(t.seller_deposit_shortfall, t.seller_payout || 0))} to your bank</span>
                </div>
              ) : (
                <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  Sale {AED(t.agreed_price)} − delivery {AED(t.delivery_fee ?? 49)}{t.packaging_upgrade_fee > 0 ? " − packaging upgrade AED 25" : ""} − SellStuff fees {AED((t.platform_commission || 0) + (t.processing_fee || 0))} = <span className="font-semibold text-slate-800">payout {AED((t.seller_payout || 0) - (t.packaging_upgrade_fee || 0))}</span>
                </div>
              )}
              {Array.isArray(t.seller_charges) && t.seller_charges.length > 0 && (
                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="seller-order-charges">
                  <p className="mb-1 text-xs font-semibold text-slate-700">Charges on this order</p>
                  <div className="space-y-0.5">
                    {t.seller_charges.map((c, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs text-slate-600" data-testid="seller-charge-row">
                        <span>{c.source === "moved_to_deposit" ? <>{c.label} AED {c.amount} — moved to deposit <span className="text-slate-400">({fmtShort(c.at)})</span></> : <>{c.label}{c.reason ? ` — ${c.reason}` : ""} <span className="text-slate-400">({c.source === "payout" ? "from payout" : "from deposit"} · {fmtShort(c.at)})</span></>}</span>
                        <span className="font-semibold text-red-600">− AED {c.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {t.status === "ESCROW_HELD" && t.collection_status === "pending" && (
                  <div className="w-full">
                    <p className="mb-2 text-xs font-medium text-slate-700" data-testid="seller-collection-prompt">{t.collection_courier_rescheduled ? "Our courier had to reschedule. Please choose a new collection time." : "Your item has sold! Please select your collection slot — you have 72 hours to confirm."}</p>
                    <Button size="sm" data-testid="seller-select-collection" className="rounded-full bg-primary" onClick={() => openSlot({ kind: "collection_slot", tid: t.id, courierString: t.collection_courier })}>Select Collection Slot</Button>
                    <button type="button" data-testid="seller-cancel-link" onClick={() => { setSellerCancel(t); setCancelReason(""); }} className="ml-3 text-xs font-medium text-slate-500 underline hover:text-red-600">Cancel this sale</button>
                  </div>
                )}
                {t.status === "ESCROW_HELD" && t.collection_status === "confirmed" && (
                  <div className="w-full space-y-1">
                    <span className="inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700" data-testid="seller-collection-set">Collection: {fmtShort(t.collection_slot?.date)} · {t.collection_slot?.label}</span>
                    {t.delivery_status !== "confirmed"
                      ? <p className="text-xs text-slate-500" data-testid="seller-awaiting-buyer">Waiting for buyer to select delivery slot.</p>
                      : <p className="text-xs text-slate-500">Delivery: {fmtShort(t.delivery_slot?.date)} · {t.delivery_slot?.label}</p>}
                  </div>
                )}
                {returnActive(t) && !t.refund_no_return && !t.return_seller_slot && !t.return_buyer_slot && (
                  <p className="w-full text-xs font-medium text-slate-500" data-testid="seller-return-awaiting-buyer">Waiting for the buyer to choose their return collection slot. You'll then pick a receipt time at least 24 hours later.</p>
                )}
                {returnActive(t) && !t.refund_no_return && !t.return_seller_slot && t.return_buyer_slot && (
                  <Button size="sm" data-testid="seller-return-slot" className="rounded-full bg-primary" onClick={() => openSlot({ kind: "return_slot_seller", tid: t.id, collectionSlot: t.return_buyer_slot })}>Select return slot</Button>
                )}
                {returnActive(t) && !t.refund_no_return && t.return_seller_slot && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600" data-testid="seller-return-set">Return receipt: {fmtShort(t.return_seller_slot.date)} · {t.return_seller_slot.label}</span>
                )}
                {t.return_to_seller_required && t.status === "CANCELLED" && !t.dr_receipt_slot && (
                  <Button size="sm" data-testid="seller-dr-receipt-btn" className="rounded-full bg-primary" onClick={() => openSlot({ kind: "dr_receipt", tid: t.id })}>Select return slot</Button>
                )}
                {t.status === "CANCELLED" && t.returned_to_seller_at && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700" data-testid="seller-returned-to-you">Returned to you: {fmtDateTime(t.returned_to_seller_at)}</span>
                )}
                {t.return_to_seller_required && t.dr_receipt_slot && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600" data-testid="seller-dr-receipt-set">Return receipt: {fmtShort(t.dr_receipt_slot.date)} · {t.dr_receipt_slot.label}</span>
                )}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {slot && <SlotPicker open mode={slot.kind === "delivery_slot" ? "delivery" : slot.kind === "collection_slot" ? "collection" : slot.kind === "return_slot_seller" ? "return_delivery" : slot.kind === "delivery_rebook" ? "delivery_rebook" : slot.kind === "dr_receipt" ? "dr_receipt" : "return"} collectionSlot={slot.collectionSlot} freeItem={slot.freeItem} courierString={slot.courierString} alreadyLeaveAtDoor={slot.alreadyLad} title={slot.kind === "delivery_slot" ? "Select your delivery time" : slot.kind === "collection_slot" ? "Select collection time" : slot.kind === "return_slot_buyer" ? "Select a return handover time" : slot.kind === "delivery_rebook" ? "Select a new delivery time" : slot.kind === "dr_receipt" ? "Select a time to receive your item" : "Select a return receipt time"}
        leaveConfig={
          slot.kind === "return_slot_buyer"
            ? { show: true, defaultOn: true, requireAck: true, label: "Leave at my door",
                terms: "Leave the item outside, ready for collection. The courier will photograph it before taking it.",
                rules: [
                  "1st attempt — free",
                  "2nd attempt — AED 49, taken from your refund",
                  "If the 3rd attempt fails, or you don't rebook within 7 days, the return is cancelled. You keep the item and the seller is paid.",
                ],
                ackLabel: "I agree to these return terms" }
            : slot.kind === "return_slot_seller"
            ? { show: true, defaultOn: false, requireAck: false, label: "Leave at my door",
                terms: "By choosing leave at door, you accept responsibility for the item once the courier has photographed it at your door." }
            : undefined
        }
        onClose={() => setSlot(null)} onSelect={submitSlot} />}
      <Dialog open={!!happy} onOpenChange={(o) => !o && setHappy(null)}>
        <DialogContent className="rounded-2xl" data-testid="happy-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm you're happy with your {happy?.listing_title}?</DialogTitle>
            <DialogDescription>This completes your purchase and releases payment to the seller. You won't be able to raise a concern afterwards.</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button data-testid="happy-confirm-yes" className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(happy.id)}>Yes, complete my purchase</Button>
            <Button data-testid="happy-confirm-back" variant="outline" className="flex-1 rounded-full" onClick={() => setHappy(null)}>Go back</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!areaFor} onOpenChange={(o) => !o && setAreaFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="area-required-dialog">
          <DialogHeader><DialogTitle>Choose your area</DialogTitle><DialogDescription>We need your area in Dubai before you can book a time slot.</DialogDescription></DialogHeader>
          <DubaiAreaSelect value={areaVal} onChange={setAreaVal} testid="area-required-select" />
          <Button data-testid="area-required-save" disabled={!areaVal} onClick={saveArea} className="mt-2 w-full rounded-full bg-primary font-semibold">Save and continue</Button>
        </DialogContent>
      </Dialog>
      <CollectionSlotConfirm
        open={!!collectionConfirm}
        courierString={collectionConfirm?.courierString}
        onConfirm={async () => { await postSlot("collection_slot", collectionConfirm.tid, { ...collectionConfirm.val, collection_terms_accepted_at: new Date().toISOString() }); setCollectionConfirm(null); }}
        onBack={() => { const tid = collectionConfirm.tid; const cs = collectionConfirm.courierString; setCollectionConfirm(null); setSlot({ kind: "collection_slot", tid, courierString: cs }); }}
      />
      <ConcernModal tid={concern?.tid} leaveAtDoor={concern?.lad} onClose={() => setConcern(null)} onDone={load} />
      <Dialog open={!!cancelConfirm} onOpenChange={(o) => !o && setCancelConfirm(null)}>
        <DialogContent className="rounded-2xl" data-testid="buyer-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel this order?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600" data-testid="buyer-cancel-body">
            We'll cancel your order for {cancelConfirm?.listing_title} and refund {AED(cancelConfirm?.total)}. Your refund will be processed by our team and you'll receive an email once it's been sent. This cannot be undone.
          </p>
          <Button data-testid="buyer-cancel-confirm" onClick={() => buyerCancel(cancelConfirm.id)} className="mt-2 w-full rounded-full bg-red-600 font-semibold hover:bg-red-700">Cancel and refund</Button>
          <button type="button" data-testid="buyer-cancel-keep" onClick={() => setCancelConfirm(null)} className="mx-auto text-sm font-medium text-slate-500 underline hover:text-slate-700">Keep my order</button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!sellerCancel} onOpenChange={(o) => { if (!o) { setSellerCancel(null); setCancelReason(""); } }}>
        <DialogContent className="rounded-2xl" data-testid="seller-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel this sale?</DialogTitle></DialogHeader>
          {sellerCancel && (() => {
            const pen = cancelPenalty(sellerCancel.total);
            const cur = sellerCancel.seller_deposit_balance ?? 0;
            const next = Math.round((cur - pen) * 100) / 100;
            return (
              <div className="space-y-3 text-sm text-slate-600">
                <p data-testid="seller-cancel-body">The buyer will be refunded in full. A cancellation fee of <b>AED {pen}</b> will be deducted from your security deposit. Balance: {fmtBal(cur)} → <span className={next < 0 ? "font-semibold text-red-600" : "font-semibold text-slate-800"}>{fmtBal(next)}</span>. This cannot be undone.</p>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Reason for cancelling</label>
                  <select data-testid="seller-cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full rounded-lg border border-slate-200 p-2 text-sm">
                    <option value="">Select a reason…</option>
                    <option value="Sold elsewhere">Sold elsewhere</option>
                    <option value="Can't find the item">Can't find the item</option>
                    <option value="Item damaged">Item damaged</option>
                    <option value="Changed my mind">Changed my mind</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <Button data-testid="seller-cancel-confirm" disabled={!cancelReason} onClick={sellerCancelSale} className="w-full rounded-full bg-red-600 font-semibold hover:bg-red-700 disabled:opacity-50">Cancel sale</Button>
                <button type="button" data-testid="seller-cancel-keep" onClick={() => { setSellerCancel(null); setCancelReason(""); }} className="mx-auto block text-sm font-medium text-slate-500 underline hover:text-slate-700">Keep this sale</button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PendingPurchaseRow({ p }) {
  const navigate = useNavigate();
  const secs = useCountdown(p.expires_at);
  const expired = secs <= 0;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4" data-testid="pending-purchase-row">
      <Thumb s={p.ai_thumbnail} />
      <div className="flex-1">
        <p className="font-medium text-slate-900">{p.title}</p>
        <p className="text-xs text-slate-500">Agreed {AED(p.agreed_price)}</p>
        {expired ? <Badge className="mt-1 rounded-full bg-slate-200 text-slate-700" data-testid="pending-expired">Expired</Badge>
          : <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-primary"><Clock className="h-3 w-3" /> <span className="font-mono">{fmt(secs)}</span> to pay</p>}
      </div>
      {!expired && <Button data-testid="pending-complete" onClick={() => navigate(`/listing/${p.listing_id}`)} className="rounded-full bg-primary font-semibold hover:bg-primary/90">Complete Purchase</Button>}
    </div>
  );
}

function PendingSaleRow({ p }) {
  const secs = useCountdown(p.expires_at);
  const expired = secs <= 0;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4" data-testid="pending-sale-row">
      <Thumb s={p.ai_thumbnail} />
      <div className="flex-1">
        <p className="font-medium text-slate-900">{p.title}</p>
        <p className="text-xs text-slate-500">Agreed {AED(p.agreed_price)}</p>
        <p className="mt-0.5 text-xs font-medium text-amber-600">Awaiting buyer payment</p>
      </div>
      {expired ? <Badge className="rounded-full bg-slate-200 text-slate-700">Relisting…</Badge>
        : <p className="flex items-center gap-1 text-xs font-medium text-slate-500"><Clock className="h-3 w-3" /> relists in <span className="font-mono">{fmt(secs)}</span></p>}
    </div>
  );
}

const OrderHead = ({ t, seller }) => (
  <div className="flex items-center gap-3">
    <Thumb s={t.ai_thumbnail} />
    <div className="flex-1">
      <p className="font-medium text-slate-900">{t.listing_title}{t.listing_type === "free" && <span data-testid="order-free-badge" className="ml-2 rounded-full px-2 py-0.5 align-middle text-[10px] font-semibold text-white" style={{ backgroundColor: "#3B63E0" }}>Free</span>}</p>
      <p className="text-xs text-slate-500">Ref CR-{(t.id || "").slice(0, 8).toUpperCase()} · {seller ? (concernUpheld(t) ? "No payout — concern upheld" : noPayout(t) ? "No payout" : t.status === "DISPUTED" ? "Payout on hold — concern under review" : isFree(t) ? "No payout" : `Payout ${AED(t.seller_payout)}`) : AED(t.total)}</p>
    </div>
    {seller && t.status === "ESCROW_HELD" && t.collection_failed_attempts === 1 && (
      <span data-testid="collection-attempt-badge" className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Collection attempt 1 of 2</span>
    )}
    <Badge className={`rounded-full ${STATUS_COLORS[t.status] || "bg-slate-100 text-slate-700"}`} data-testid="order-status-badge">{displayStatus(t)}</Badge>
  </div>
);
function buyerStatusKey(t) {
  if (t.status === "COMPLETED") return "completed";
  if (t.status === "CANCELLED") return "cancelled";
  if (t.status === "REFUNDED") return "refunded";
  if (t.status === "DISPUTED") return "disputed";
  if (t.collection_courier_rescheduled && t.collection_status !== "collected") return "collection_rescheduled";
  if (t.delivery_rebook_required || t.logistics_status === "delivery_failed") return "delivery_failed";
  const delivered = t.logistics_status === "delivered" || t.delivery_status === "delivered";
  if (delivered) return t.no_return ? "delivered_door" : "delivered_inspect";
  if (t.collection_status === "collected" && t.delivery_failed_at && t.delivery_status === "confirmed") return "delivery_rebooked";
  if (t.logistics_status === "out_for_delivery" || t.collection_status === "collected") return "out_for_delivery";
  if (t.delivery_status === "confirmed") return "delivery_slot_confirmed";
  const sellerConfirmed = !!t.collection_slot || ["confirmed", "collected"].includes(t.collection_status) || ["collection_scheduled", "out_for_delivery", "delivered", "completed"].includes(t.logistics_status);
  if (sellerConfirmed) return "awaiting_delivery_slot";
  return "awaiting_collection_slot";
}

function buyerMessage(t, dd, dw) {
  switch (buyerStatusKey(t)) {
    case "awaiting_collection_slot": return "We are waiting for the seller to confirm their collection slot. You will be notified as soon as it is confirmed — usually within a few hours.";
    case "collection_rescheduled": return "Waiting for the seller to choose a new collection time. We'll ask you to choose your delivery time after that.";
    case "delivery_rebooked": return `Your new delivery is booked for ${dd ? fmtShort(dd) : "your selected date"} · ${dw || "your selected time"}${t.leave_at_door ? " — it will be left at your door." : ". Please be present to receive it."}`;
    case "delivery_slot_confirmed": return `Your delivery is scheduled for ${dd ? fmtShort(dd) : "your selected date"} · ${dw || "your selected time"}. We will notify you when your item has been collected.`;
    case "out_for_delivery": return "Your item is out for delivery and will arrive during your selected time slot.";
    case "delivered_door": return "Your item has been left at your door. As you selected leave-at-door delivery, this sale is now complete.";
    case "completed": return "This purchase is complete. Thank you for using SellStuff!";
    case "cancelled": return "This order was cancelled. If you were charged, your refund will appear within 5 to 10 business days.";
    case "refunded": return "You have been refunded for this order. The amount will appear in your account within 5 to 10 business days.";
    default: return "";
  }
}

function BalanceOwedNote({ t, owing }) {
  const [busy, setBusy] = useState(false);
  const amt = Math.round((t.balance_owed_created || 0) * 100) / 100;
  return (
    <div className="w-full rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-700" data-testid="buyer-balance-owed-note">
      {owing ? `No refund — you owe AED ${amt} for the delivery and return.` : `No refund — the AED ${amt} for the delivery and return has been settled.`}
      {owing && <Button size="sm" disabled={busy} data-testid="order-settle-balance-btn" className="ml-2 mt-1 h-7 rounded-full bg-red-600 px-3 text-xs hover:bg-red-700" onClick={async () => { setBusy(true); await settleBalance(); setBusy(false); }}>{busy ? "Starting…" : "Settle balance"}</Button>}
    </div>
  );
}

const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500">{text}</div>;

function CollectionSlotConfirm({ open, onConfirm, onBack, courierString }) {
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { if (!open) setAgreed(false); }, [open]);
  const points = [
    "I'll be available at the selected time to hand over my item",
    "My item is the exact item listed, and ready for collection",
    "My item is under 5kg and fits the packaging I selected — or is under 150cm in length if I chose Wrapped",
    "If it doesn't fit, the courier may offer a packaging upgrade — I can accept for AED 25, deducted from my payout, or decline, in which case a failed collection fee of AED 49 applies",
    "If I'm unavailable or my item isn't ready, a failed collection fee of AED 49 will be deducted from my deposit, and a second failed collection will cancel the order and suspend my seller account",
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onBack()}>
      <DialogContent className="rounded-2xl" data-testid="collection-confirm-dialog">
        <DialogHeader><DialogTitle>Confirm your collection slot</DialogTitle></DialogHeader>
        {courierString && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="collection-courier-string">
            <span className="font-semibold text-slate-700">Collect from:</span> {courierString}
          </div>
        )}
        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-slate-200 p-3 text-sm leading-snug text-slate-700">
          <input type="checkbox" checked={agreed} onChange={() => setAgreed((v) => !v)} data-testid="collection-confirm-check" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold text-slate-800">I confirm that:</span>
            <ul className="mt-1.5 list-disc space-y-1 pl-4">
              {points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </span>
        </label>
        <Button data-testid="collection-confirm-btn" disabled={!agreed} onClick={onConfirm} className="mt-2 w-full rounded-full bg-primary font-semibold">Confirm my collection slot</Button>
        <button type="button" data-testid="collection-confirm-back" onClick={onBack} className="mx-auto text-sm font-medium text-slate-500 underline hover:text-slate-700">Go back — change my slot</button>
      </DialogContent>
    </Dialog>
  );
}
